// ArticulosSection.js — Sección `articulos`: el hub de contenido (tipo blog).
//
// En la página del hub (p. ej. /actualizacion) pinta las pestañas de categorías,
// las tarjetas y la paginación. En /actualizacion/:articulo pinta el artículo:
// título, fecha, cuerpo y video. Los artículos y categorías se administran en la
// ficha del proyecto → 📰 Hub de contenido; aquí solo se configura el aspecto.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { articuloService, BACKEND_BASE } from '../../../services/api';
import { resolverVideo } from './VideoSection';

const FUENTE_TARJETA = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const urlImagen = (v) => (!v ? null : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
// "2026-04-29" → "Abr 29, 2026" (formato del sitio original, sin zona horaria).
export const fechaCorta = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${MESES[+m[2] - 1]} ${+m[3]}, ${m[1]}` : '';
};

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  @media (max-width: 980px) { padding: ${p => p.$paddingMovil}; }
`;

const Fila = styled.div`
  width: 80%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  font-family: ${FUENTE_TARJETA};
  @media (max-width: 767px) { width: 88%; }
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 16px;
  align-items: center;
`;

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  border: 1px solid ${p => (p.$activo ? 'rgba(0,0,0,.2)' : p.$acento)};
  background: ${p => (p.$activo ? p.$acento : p.$fondo)};
  color: ${p => (p.$activo ? '#fff' : p.$acento)};
  transition: transform .08s ease, box-shadow .2s ease, background .2s ease, color .2s ease;
  &:hover { box-shadow: 0 6px 18px rgba(15,23,42,.10); transform: translateY(-1px); }
  @media (max-width: 991px) { padding: 10px 8px; font-size: 12px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$columnas}, minmax(0, 1fr));
  gap: 16px;
  transition: opacity .2s ease;
  opacity: ${p => (p.$cargando ? 0.6 : 1)};
  @media (max-width: 960px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Tarjeta = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(15,23,42,.06);
  transition: transform .12s ease, box-shadow .2s ease, border-color .2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(15,23,42,.10); border-color: rgba(15,23,42,.14); }
`;

const Media = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${p => p.$img ? '#f8fafc' : 'linear-gradient(135deg, rgba(99,102,241,.14), rgba(168,85,247,.14))'};
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const Badge = styled.span`
  position: absolute;
  right: 7px;
  bottom: 8px;
  padding: 11px 10px;
  border-radius: 999px;
  background: ${p => p.$color};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const Cuerpo = styled.div`
  padding: 14px 14px 16px;
  h3 {
    margin: 0 0 8px;
    padding: 0 0 10px;
    font-family: ${p => p.$fuenteTitulo};
    font-size: ${p => p.$sizeTitulo};
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: ${p => p.$colorTitulo};
  }
  p { margin: 0; color: #808080; font-size: 14px; line-height: 1.45; }
  @media (max-width: 640px) { h3 { font-size: 20px; } }
`;

const Paginacion = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  button {
    min-width: 38px; height: 38px; padding: 0 12px;
    border-radius: 12px; border: 1px solid rgba(15,23,42,.10);
    background: #fff; color: #0f172a; font-weight: 700; font-family: inherit; cursor: pointer;
    &:hover { box-shadow: 0 6px 18px rgba(15,23,42,.10); transform: translateY(-1px); }
    &[aria-current='page'] { background: ${p => p.$acento}; color: #fff; border-color: rgba(0,0,0,.2); }
  }
`;

const Nota = styled.p`
  margin: 52px 0 0;
  text-align: center;
  color: #4d4d4d;
  font-size: 14px;
  line-height: 1.4em;
  font-family: Arial, Helvetica, sans-serif;
  white-space: pre-line;
`;

const Vacio = styled.div`
  padding: 16px;
  border: 1px dashed rgba(15,23,42,.18);
  border-radius: 14px;
  background: rgba(248,250,252,.7);
  color: #475569;
`;

// ── Detalle ──────────────────────────────────────────────────────────────
const Ficha = styled.article`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  background: #fff;
  border-radius: 23px;
  padding: 40px 40px 60px;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
  h1 {
    margin: 0 0 6px;
    padding: 0;
    font-family: 'Roboto', Helvetica, Arial, sans-serif;
    font-size: ${p => p.$sizeTitulo};
    font-weight: 500;
    line-height: 1.25;
    color: ${p => p.$colorTitulo};
  }
  .fecha { margin: 0 0 18px; color: #666; font-size: 18px; }
  .contenido {
    color: #808080;
    font-size: 18px;
    line-height: 1.5;
    p { margin: 0 0 1em; }
    p:empty { display: none; }
    img { max-width: 100%; height: auto; border-radius: 8px; }
    a { color: ${p => p.$acento}; }
    h2, h3, h4 { font-family: 'Roboto', Helvetica, Arial, sans-serif; color: ${p => p.$colorTitulo}; }
  }
  @media (max-width: 767px) {
    padding: 24px 20px 36px;
    h1 { font-size: 22px; }
    .fecha, .contenido { font-size: 16px; }
  }
`;

const Video = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  margin-top: 22px;
  background: #0f0a1a;
  iframe, video { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
`;

const Volver = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 28px;
  color: ${p => p.$acento};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Cargando = styled.div`
  text-align: center;
  color: #7c6b8a;
  padding: 40px 0;
`;

const DetalleArticulo = ({ c, proyecto, slug, base }) => {
  const [estado, setEstado] = useState('cargando');
  const [art, setArt] = useState(null);

  useEffect(() => {
    if (!proyecto?.id) return undefined;
    let cancel = false;
    setEstado('cargando');
    articuloService.obtener(proyecto.id, slug)
      .then(({ data }) => { if (!cancel) { setArt(data.articulo); setEstado('ok'); } })
      .catch(() => { if (!cancel) setEstado('no'); });
    return () => { cancel = true; };
  }, [proyecto?.id, slug]);

  useEffect(() => {
    if (art?.titulo) document.title = `${art.titulo} — ${proyecto?.nombre || ''}`;
  }, [art, proyecto?.nombre]);

  const acento = c.color_acento || '#742db4';
  const video = resolverVideo(art?.video_url);

  return (
    <Ficha $ancho={c.ancho_articulo || '700px'} $sizeTitulo={c.tamano_titulo_articulo || '26px'} $colorTitulo={c.color_titulo_articulo || '#0f3d6e'} $acento={acento}>
      {estado === 'cargando' && <Cargando>Cargando…</Cargando>}
      {estado === 'no' && (
        <>
          <Cargando>Este contenido no está disponible.</Cargando>
          <Volver to={base} $acento={acento}>← {c.texto_volver || 'Volver al hub'}</Volver>
        </>
      )}
      {estado === 'ok' && art && (
        <>
          <h1>{art.titulo}</h1>
          {c.mostrar_fecha !== false && art.fecha_publicacion && <p className="fecha">{fechaCorta(art.fecha_publicacion)}</p>}
          {art.contenido
            ? <div className="contenido" dangerouslySetInnerHTML={{ __html: art.contenido }} />
            : (art.extracto && <div className="contenido"><p>{art.extracto}</p></div>)}
          {video && (
            <Video>
              {video.tipo === 'archivo'
                ? <video src={video.src} controls playsInline />
                : <iframe src={video.src} title={art.titulo} allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" allowFullScreen />}
            </Video>
          )}
          {c.mostrar_volver === true && <Volver to={base} $acento={acento}>← {c.texto_volver || 'Volver al hub'}</Volver>}
        </>
      )}
    </Ficha>
  );
};

const ArticulosSection = ({ config = {}, proyecto, tl = (v, d = '') => v || d }) => {
  const c = config;
  const { slug: slugPagina, articulo } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Ruta base de los artículos: la de la página donde vive la sección, salvo que
  // se fije otra (p. ej. si la sección está en la portada).
  const base = c.ruta_base
    ? `/${String(c.ruta_base).replace(/^\/+|\/+$/g, '')}`
    : `/${slugPagina || 'actualizacion'}`;

  const params = new URLSearchParams(location.search);
  const categoria = params.get('categoria') || 'todos';
  const pagina = Math.max(1, parseInt(params.get('pagina'), 10) || 1);

  const [datos, setDatos] = useState({ categorias: [], articulos: [], paginas: 1 });
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (articulo || !proyecto?.id) return undefined;
    let cancel = false;
    setCargando(true);
    articuloService.listar(proyecto.id, {
      coleccion: c.coleccion || 'hub',
      categoria,
      pagina,
      por_pagina: c.por_pagina || 9,
    })
      .then(({ data }) => { if (!cancel) setDatos(data); })
      .catch(() => { if (!cancel) setDatos({ categorias: [], articulos: [], paginas: 1 }); })
      .finally(() => { if (!cancel) setCargando(false); });
    return () => { cancel = true; };
  }, [articulo, proyecto?.id, c.coleccion, c.por_pagina, categoria, pagina]);

  const irA = (cat, pag = 1) => {
    const q = new URLSearchParams();
    if (cat && cat !== 'todos') q.set('categoria', cat);
    if (pag > 1) q.set('pagina', String(pag));
    const s = q.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ''}`, { replace: true });
  };

  const acento = c.color_acento || '#742db4';
  const paginas = useMemo(() => Array.from({ length: datos.paginas || 1 }, (_, i) => i + 1), [datos.paginas]);

  const seccion = (hijos) => (
    <Seccion
      $fondo={c.color_fondo || '#f8f5ff'}
      $padding={articulo ? (c.padding_articulo || '60px 0') : (c.padding || '90px 0 62px')}
      $paddingMovil={c.padding_movil || '40px 0'}
    >
      {hijos}
    </Seccion>
  );

  if (articulo) return seccion(<DetalleArticulo c={c} proyecto={proyecto} slug={articulo} base={base} />);

  const nota = tl(c.nota_pie, '');

  return seccion(
    <Fila $ancho={c.ancho_maximo || '1200px'}>
      {c.mostrar_filtros !== false && datos.categorias.length > 0 && (
        <Filtros role="tablist" aria-label="Filtrar por categoría">
          <Pill type="button" role="tab" aria-selected={categoria === 'todos'} $activo={categoria === 'todos'}
            $acento={acento} $fondo={c.color_pill || '#eef2ff'} onClick={() => irA('todos')}>
            {tl(c.texto_todos, 'Todos')}
          </Pill>
          {datos.categorias.map(cat => (
            <Pill key={cat.slug} type="button" role="tab" aria-selected={categoria === cat.slug} $activo={categoria === cat.slug}
              $acento={acento} $fondo={c.color_pill || '#eef2ff'} onClick={() => irA(cat.slug)}>
              {cat.nombre}
            </Pill>
          ))}
        </Filtros>
      )}

      {!cargando && datos.articulos.length === 0 ? (
        <Vacio>{tl(c.texto_vacio, 'Aún no hay contenidos publicados en esta categoría.')}</Vacio>
      ) : (
        <Grid $columnas={c.columnas || 3} $cargando={cargando} aria-busy={cargando}>
          {datos.articulos.map(a => {
            const img = urlImagen(a.imagen);
            return (
              <Tarjeta key={a.id} to={`${base}/${a.slug}`} aria-label={a.titulo}>
                <Media $img={!!img}>
                  {img && <img src={img} alt={a.titulo} loading="lazy" decoding="async" />}
                  {c.mostrar_badge !== false && a.categoria && <Badge $color={acento}>{a.categoria}</Badge>}
                </Media>
                <Cuerpo
                  $fuenteTitulo={c.fuente_titulo || "'Roboto', Helvetica, Arial, sans-serif"}
                  $sizeTitulo={c.tamano_titulo || '24px'}
                  $colorTitulo={c.color_titulo || '#342547'}
                >
                  <h3>{a.titulo}</h3>
                  {c.mostrar_extracto !== false && a.extracto && <p>{a.extracto}</p>}
                </Cuerpo>
              </Tarjeta>
            );
          })}
        </Grid>
      )}

      {paginas.length > 1 && (
        <Paginacion $acento={acento} aria-label="Paginación">
          {paginas.map(n => (
            <button key={n} type="button" aria-current={n === pagina ? 'page' : undefined} onClick={() => irA(categoria, n)}>{n}</button>
          ))}
        </Paginacion>
      )}

      {nota && <Nota>{nota}</Nota>}
    </Fila>
  );
};

export default ArticulosSection;
